'use client';

import React from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { BreadcrumbItem } from '@/components/ui/layout/fixed-layout';
import { Logo } from '@/components/ui/logo';
import { useTenant } from '@/contexts/TenantContext';

interface BreadcrumbProps {
  breadcrumbs?: BreadcrumbItem[];
  mobile?: boolean;
  inverted?: boolean;
}

const truncateText = (text: string, maxLength: number = 30): string => {
  if (!text || text.length <= maxLength) return text;

  const halfLength = Math.floor((maxLength - 3) / 2);
  const start = text.substring(0, halfLength);
  const end = text.substring(text.length - halfLength);

  return `${start}...${end}`;
};

export function Breadcrumb({ breadcrumbs, mobile = false, inverted = false }: BreadcrumbProps) {
  const pathname = usePathname();
  const { isOrkhan, shortName } = useTenant();
  const isHome = pathname === '/';

  const homeLink = isHome && isOrkhan ? (
    <Link href="/" className="hover:opacity-70 transition-opacity flex items-center">
      <Logo width={14} height={14} className={inverted ? 'fill-black' : 'fill-white'} />
    </Link>
  ) : (
    <Link href="/" className={`${inverted ? 'hover:text-zinc-600' : 'hover:text-zinc-300'} transition-colors`}>
      {shortName}
    </Link>
  );

  if (breadcrumbs && breadcrumbs.length > 0) {
    return (
      <div className={`font-mono uppercase text-[12px] ${inverted ? 'text-black' : 'text-white'} flex items-center`}>
        {homeLink}

        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {mobile ? (
              // Mobile: show only the last item
              index === breadcrumbs.length - 1 && (
                <>
                  <span className={`mx-1 ${inverted ? 'text-zinc-400' : 'text-zinc-500'}`}>/</span>
                  <span className={item.isActive ? (inverted ? 'text-zinc-600' : 'text-zinc-300') : ''}>{truncateText(item.label)}</span>
                </>
              )
            ) : (
              // Desktop: show all items
              <>
                <span className={`mx-1 ${inverted ? 'text-zinc-400' : 'text-zinc-500'}`}>/</span>
                {item.href ? (
                  <Link href={item.href} className={`${inverted ? 'hover:text-zinc-600' : 'hover:text-zinc-300'} transition-colors`}>
                    {truncateText(item.label)}
                  </Link>
                ) : (
                  <span className={item.isActive ? (inverted ? 'text-zinc-600' : 'text-zinc-300') : ''}>{truncateText(item.label)}</span>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className={`font-mono uppercase text-[12px] ${inverted ? 'text-black' : 'text-white'} flex items-center`}>
      {homeLink}
    </div>
  );
}
